import React, { useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2, CheckCircle2, Calendar, X, Archive } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/** Excel / CSV dataset exports. All downloads go through the authenticated
 *  axios client so JWT is attached automatically. A date-range selector at the
 *  top scopes every download (start_date / end_date query params — inclusive).
 *  `warehouses` is a live rollup and always exports as-of-now regardless of dates. */
const REPORTS = [
  { kind: "lots",             label: "Stock Lots",       hint: "Every lot created inside the date range: category, colour, size, rack, warehouse, factory, barcode." },
  { kind: "movements",        label: "Stock Movements",  hint: "Every IN / OUT / rerack whose timestamp falls inside the date range." },
  { kind: "purchase_orders",  label: "Purchase Orders",  hint: "Every PO whose order date falls inside the date range, with line items and packing progress." },
  { kind: "warehouses",       label: "Warehouse Summary", hint: "Live per-warehouse rollup (as of now). Ignores date range by design." },
];

/** Local YYYY-MM-DD helper — avoids the UTC-offset one-day skew you get from
 *  `new Date().toISOString().slice(0,10)` when the user is east of UTC. */
const ymd = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const PRESETS = [
  { key: "today",   label: "Today" },
  { key: "week",    label: "This Week" },
  { key: "month",   label: "This Month" },
  { key: "all",     label: "All Time" },
  { key: "custom",  label: "Custom" },
];

function computeRange(preset) {
  const now = new Date();
  if (preset === "today") {
    const s = ymd(now); return { start: s, end: s };
  }
  if (preset === "week") {
    const day = now.getDay() || 7; // Mon=1..Sun=7
    const start = new Date(now); start.setDate(now.getDate() - (day - 1));
    return { start: ymd(start), end: ymd(now) };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: ymd(start), end: ymd(now) };
  }
  return { start: "", end: "" };
}

export default function Reports() {
  const [busy, setBusy] = useState(null); // `${kind}:${fmt}`
  const [lastAt, setLastAt] = useState({}); // { [kind]: iso }
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });

  // Recompute the date bounds whenever a preset (other than Custom) is picked.
  useEffect(() => {
    if (preset === "custom") return;
    setRange(computeRange(preset));
  }, [preset]);

  const activeRange = useMemo(() => ({
    start_date: range.start || undefined,
    end_date: range.end || undefined,
  }), [range]);

  const rangeLabel = useMemo(() => {
    if (!range.start && !range.end) return "All time";
    if (range.start && range.end && range.start === range.end) return range.start;
    return `${range.start || "…"} → ${range.end || "…"}`;
  }, [range]);

  const download = async (kind, fmt) => {
    const key = `${kind}:${fmt}`;
    setBusy(key);
    try {
      const params = { kind, ...activeRange };
      // Warehouses report is a live snapshot — strip the date range from the
      // request so the user isn't misled into thinking it's date-scoped.
      if (kind === "warehouses") { delete params.start_date; delete params.end_date; }
      const r = await api.get(`/reports/export.${fmt}`, {
        params, responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: fmt === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = (range.start || range.end)
        ? `-${range.start || "start"}_${range.end || "end"}` : "";
      a.download = `veneer-slate-${kind}${suffix}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setLastAt(m => ({ ...m, [kind]: new Date().toISOString() }));
      toast.success(`${kind} exported as ${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  const clearDates = () => { setPreset("all"); setRange({ start: "", end: "" }); };

  // ---- One-click full backup: multi-sheet .xlsx spanning every dataset
  //      plus a Photos sheet with clickable public URLs. Honors the selected
  //      date range for time-based datasets; warehouses + photos always
  //      export as-of-now. ----
  const downloadFullBackup = async () => {
    setBusy("full:xlsx");
    try {
      const r = await api.get(`/reports/export.full.xlsx`, {
        params: activeRange, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const stamp = (range.start || range.end)
        ? `-${range.start || "start"}_${range.end || "end"}` : "";
      const a = document.createElement("a");
      a.href = url;
      a.download = `veneer-slate-full-backup${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setLastAt(m => ({ ...m, full: new Date().toISOString() }));
      toast.success("Full backup downloaded · every dataset + photo URLs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Full backup failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Data exports</div>
        <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">Reports & downloads.</h1>
        <p className="text-[#5D5148] mt-2 text-sm max-w-2xl">
          Pull the full dataset out of Veneer Slate at any time — as Excel workbooks for analysis
          or CSV for lightweight tooling. Downloads reflect live data at the moment of export.
        </p>
      </header>

      {/* Date range selector — presets + custom pickers. Applies to every export
          below except the live warehouse rollup. */}
      <div className="bg-white border border-[#E5D8C4] p-4 md:p-5" data-testid="reports-date-range">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">
            <Calendar size={13}/> Date range
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => setPreset(p.key)}
                data-testid={`reports-preset-${p.key}`}
                className={`h-9 px-3 text-[11px] uppercase tracking-[0.2em] border ${preset === p.key
                  ? "bg-[#2D2622] text-white border-[#2D2622]"
                  : "bg-white text-[#5D5148] border-[#E5D8C4] hover:border-[#2D2622]"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <label className="flex items-center gap-1 text-[11px] text-[#8B6F47]">
              <span className="uppercase tracking-widest font-semibold">Start</span>
              <input type="date" value={range.start}
                data-testid="reports-start-date"
                onChange={(e) => { setPreset("custom"); setRange(r => ({ ...r, start: e.target.value })); }}
                className="h-9 px-2 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
            </label>
            <label className="flex items-center gap-1 text-[11px] text-[#8B6F47]">
              <span className="uppercase tracking-widest font-semibold">End</span>
              <input type="date" value={range.end}
                data-testid="reports-end-date"
                onChange={(e) => { setPreset("custom"); setRange(r => ({ ...r, end: e.target.value })); }}
                className="h-9 px-2 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
            </label>
            {(range.start || range.end) && (
              <button onClick={clearDates} data-testid="reports-clear-range"
                className="h-9 px-2 text-[11px] uppercase tracking-widest text-[#8B6F47] hover:text-[#2D2622] inline-flex items-center gap-1">
                <X size={12}/> Clear
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 text-[11px] text-[#8B6F47]" data-testid="reports-range-label">
          Exports scoped to <b className="text-[#2D2622]">{rangeLabel}</b>. Warehouse Summary always exports as-of-now.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {REPORTS.map(r => {
          const last = lastAt[r.kind];
          return (
            <div key={r.kind} className="bg-white border border-[#E5D8C4] p-5 md:p-6 flex flex-col"
              data-testid={`report-card-${r.kind}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">Dataset</div>
                  <div className="font-display font-bold text-xl text-[#2D2622] mt-1">{r.label}</div>
                </div>
                <Download size={18} className="text-[#C4A57B]" />
              </div>
              <p className="text-xs text-[#5D5148] mt-3 flex-1">{r.hint}</p>

              <div className="grid grid-cols-2 gap-2 mt-4">
                <button onClick={() => download(r.kind, "xlsx")} disabled={!!busy}
                  data-testid={`report-xlsx-${r.kind}`}
                  className="h-11 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E] disabled:opacity-40 inline-flex items-center justify-center gap-2">
                  {busy === `${r.kind}:xlsx`
                    ? <Loader2 className="animate-spin" size={14}/>
                    : <FileSpreadsheet size={14}/>}
                  Excel (.xlsx)
                </button>
                <button onClick={() => download(r.kind, "csv")} disabled={!!busy}
                  data-testid={`report-csv-${r.kind}`}
                  className="h-11 px-4 border border-[#E5D8C4] bg-white text-[#2D2622] text-xs uppercase tracking-[0.2em] hover:border-[#2D2622] disabled:opacity-40 inline-flex items-center justify-center gap-2">
                  {busy === `${r.kind}:csv`
                    ? <Loader2 className="animate-spin" size={14}/>
                    : <FileText size={14}/>}
                  CSV
                </button>
              </div>

              {last && (
                <div className="mt-3 flex items-center gap-2 text-[11px] text-[#4D7C0F]"
                  data-testid={`report-last-${r.kind}`}>
                  <CheckCircle2 size={13}/> Last downloaded {new Date(last).toLocaleString()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-[#8B6F47] italic">
        Tip: XLSX preserves formatting and freezes the header row — best for offline review or
        emailing to accounting. CSV is compact and opens in every tool.
      </div>
    </div>
  );
}
